import React, { useMemo, useState } from "react";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import { PDFDocument } from "pdf-lib";
import { toast } from "sonner";
import {
  QrCode, FileText, Image as ImageIcon, FileStack, Type, KeyRound, Tags, Printer, Download, Copy, Sparkles, Lock,
} from "lucide-react";
import { useTool, startProCheckout } from "@/lib/pro";
import { MORE_TOOLS } from "./moreTools";
import { EXTRA_TOOLS } from "./microTools";
import { MICRO_TOOLS_2, CUSTOM_TOOLS_2 } from "./microTools2";

const input = "w-full border border-slate-200 bg-white rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors";
const label = "text-xs font-semibold uppercase tracking-widest text-slate-500 block mb-2";
const btn = "inline-flex items-center gap-2 bg-indigo-600 text-white rounded-lg px-5 py-2.5 font-medium hover:bg-indigo-700 active:scale-95 transition disabled:opacity-50";
const ghost = "inline-flex items-center gap-2 border border-slate-200 rounded-lg px-5 py-2.5 font-medium hover:bg-slate-50 active:scale-95 transition";

function ProGate({ tool, freeLimit, children }) {
  const t = useTool(tool, freeLimit);
  const blocked = !t.canUse;
  return (
    <div>
      {!t.pro && (
        <div className="flex items-center justify-between text-sm text-slate-500 mb-4 border border-slate-100 rounded-lg px-4 py-2.5 bg-slate-50/60">
          <span data-testid="tool-usage">Free uses today: {Math.min(t.count, freeLimit)} / {freeLimit}</span>
          <button onClick={startProCheckout} className="text-indigo-600 font-medium inline-flex items-center gap-1 hover:underline"><Sparkles className="w-3.5 h-3.5" /> Go Pro ($9)</button>
        </div>
      )}
      {blocked ? (
        <div data-testid="tool-locked" className="text-center border-2 border-dashed border-slate-200 rounded-xl p-12 bg-slate-50/50">
          <Lock className="w-10 h-10 text-indigo-500 mx-auto mb-4" />
          <h3 className="font-display text-2xl mb-2">Daily free limit reached</h3>
          <p className="text-slate-500 mb-6">Upgrade to Pro for unlimited use, no watermarks, and higher limits — one-time $9.</p>
          <button data-testid="upgrade-pro" onClick={startProCheckout} className={btn + " mx-auto"}><Sparkles className="w-4 h-4" /> Unlock Pro</button>
        </div>
      ) : children(t)}
    </div>
  );
}

/* ---------------- QR CODE ---------------- */
function QRTool() {
  const [text, setText] = useState("https://juliusmason.com");
  const [fg, setFg] = useState("#4f46e5");
  const [bg, setBg] = useState("#ffffff");
  const [url, setUrl] = useState("");
  const gen = async (t) => {
    if (!t.canUse) return;
    try {
      const d = await QRCode.toDataURL(text || " ", { width: 600, margin: 2, color: { dark: fg, light: bg } });
      setUrl(d); t.inc();
    } catch { toast.error("Could not generate QR"); }
  };
  return (
    <ProGate tool="qr" freeLimit={10}>
      {(t) => (
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-5">
            <div><label className={label}>Text or URL</label><input data-testid="qr-text" className={input} value={text} onChange={(e) => setText(e.target.value)} /></div>
            <div className="flex gap-4">
              <div><label className={label}>Foreground</label><input type="color" value={fg} onChange={(e) => setFg(e.target.value)} className="h-11 w-16 rounded border border-slate-200" /></div>
              <div><label className={label}>Background</label><input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="h-11 w-16 rounded border border-slate-200" /></div>
            </div>
            <button data-testid="qr-generate" onClick={() => gen(t)} className={btn}><QrCode className="w-4 h-4" /> Generate</button>
          </div>
          <div className="flex flex-col items-center justify-center border border-slate-100 rounded-xl p-6 bg-slate-50/40">
            {url ? (<><img data-testid="qr-result" src={url} alt="QR" className="w-56 h-56" /><a href={url} download="qr-code.png" className={ghost + " mt-4"}><Download className="w-4 h-4" /> Download PNG</a></>) : <p className="text-slate-400">Your QR code appears here</p>}
          </div>
        </div>
      )}
    </ProGate>
  );
}

/* ---------------- INVOICE ---------------- */
function InvoiceTool() {
  const [inv, setInv] = useState({ from: "Julius Mason", to: "", number: "INV-001", date: new Date().toISOString().slice(0, 10) });
  const [items, setItems] = useState([{ desc: "Service", qty: 1, price: 100 }]);
  const total = items.reduce((s, i) => s + Number(i.qty) * Number(i.price), 0);
  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const make = (t) => {
    if (!t.canUse) return;
    const doc = new jsPDF();
    doc.setFontSize(22); doc.setTextColor("#4f46e5"); doc.text("INVOICE", 20, 25);
    doc.setFontSize(10); doc.setTextColor("#0f172a");
    doc.text(`From: ${inv.from}`, 20, 40); doc.text(`To: ${inv.to}`, 20, 47);
    doc.text(`Invoice #: ${inv.number}`, 150, 40); doc.text(`Date: ${inv.date}`, 150, 47);
    let y = 65; doc.setFont(undefined, "bold");
    doc.text("Description", 20, y); doc.text("Qty", 120, y); doc.text("Price", 145, y); doc.text("Total", 175, y);
    doc.setFont(undefined, "normal"); y += 8;
    items.forEach((it) => { doc.text(String(it.desc), 20, y); doc.text(String(it.qty), 120, y); doc.text(`$${Number(it.price).toFixed(2)}`, 145, y); doc.text(`$${(it.qty * it.price).toFixed(2)}`, 175, y); y += 8; });
    y += 4; doc.setFont(undefined, "bold"); doc.text(`TOTAL: $${total.toFixed(2)}`, 145, y);
    if (!t.pro) { doc.setFontSize(8); doc.setTextColor("#94a3b8"); doc.text("Made with Julius Mason Tools — go Pro to remove this", 20, 285); }
    doc.save(`${inv.number}.pdf`); t.inc(); toast.success("Invoice downloaded");
  };
  return (
    <ProGate tool="invoice" freeLimit={3}>
      {(t) => (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={label}>From</label><input className={input} value={inv.from} onChange={(e) => setInv({ ...inv, from: e.target.value })} /></div>
            <div><label className={label}>Bill To</label><input data-testid="inv-to" className={input} value={inv.to} onChange={(e) => setInv({ ...inv, to: e.target.value })} placeholder="Client name" /></div>
            <div><label className={label}>Invoice #</label><input className={input} value={inv.number} onChange={(e) => setInv({ ...inv, number: e.target.value })} /></div>
            <div><label className={label}>Date</label><input type="date" className={input} value={inv.date} onChange={(e) => setInv({ ...inv, date: e.target.value })} /></div>
          </div>
          <div className="space-y-2">
            <label className={label}>Line items</label>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <input className={input + " col-span-6"} value={it.desc} onChange={(e) => setItem(i, "desc", e.target.value)} placeholder="Description" />
                <input type="number" className={input + " col-span-2"} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                <input type="number" className={input + " col-span-3"} value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} />
                <button onClick={() => setItems(items.filter((_, x) => x !== i))} className="col-span-1 text-slate-400 hover:text-red-500">×</button>
              </div>
            ))}
            <button onClick={() => setItems([...items, { desc: "", qty: 1, price: 0 }])} className={ghost}>+ Add item</button>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <span className="font-display text-2xl">Total: <span className="text-indigo-600">${total.toFixed(2)}</span></span>
            <button data-testid="inv-download" onClick={() => make(t)} className={btn}><Download className="w-4 h-4" /> Download PDF</button>
          </div>
        </div>
      )}
    </ProGate>
  );
}

/* ---------------- IMAGE ---------------- */
function ImageTool() {
  const [file, setFile] = useState(null);
  const [width, setWidth] = useState(1000);
  const [format, setFormat] = useState("image/jpeg");
  const [quality, setQuality] = useState(0.8);
  const [out, setOut] = useState(null);
  const process = (t) => {
    if (!file || !t.canUse) return;
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, width / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale; canvas.height = img.height * scale;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => { setOut({ url: URL.createObjectURL(blob), size: blob.size, ext: format.split("/")[1] }); t.inc(); }, format, quality);
    };
    img.src = URL.createObjectURL(file);
  };
  return (
    <ProGate tool="image" freeLimit={10}>
      {(t) => (
        <div className="space-y-5">
          <label className="block border-2 border-dashed border-slate-200 bg-slate-50/50 hover:bg-slate-50 rounded-xl p-8 text-center cursor-pointer transition-colors">
            <ImageIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            {file ? file.name : "Click to upload an image"}
            <input data-testid="img-upload" type="file" accept="image/*" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setOut(null); }} />
          </label>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className={label}>Max width (px)</label><input type="number" className={input} value={width} onChange={(e) => setWidth(Number(e.target.value))} /></div>
            <div><label className={label}>Format</label><select className={input} value={format} onChange={(e) => setFormat(e.target.value)}><option value="image/jpeg">JPEG</option><option value="image/png">PNG</option><option value="image/webp">WebP</option></select></div>
            <div><label className={label}>Quality ({Math.round(quality * 100)}%)</label><input type="range" min="0.1" max="1" step="0.05" value={quality} onChange={(e) => setQuality(Number(e.target.value))} className="w-full mt-3 accent-indigo-600" /></div>
          </div>
          <button data-testid="img-process" onClick={() => process(t)} disabled={!file} className={btn}>Compress & Convert</button>
          {out && (<div className="border border-slate-100 rounded-xl p-4 flex items-center gap-4"><img src={out.url} alt="out" className="w-24 h-24 object-cover rounded" /><div><p className="text-sm text-slate-500">{(out.size / 1024).toFixed(1)} KB · {out.ext.toUpperCase()}</p><a href={out.url} download={`image.${out.ext}`} className={ghost + " mt-2"}><Download className="w-4 h-4" /> Download</a></div></div>)}
        </div>
      )}
    </ProGate>
  );
}

/* ---------------- PDF ---------------- */
function PDFTool() {
  const [files, setFiles] = useState([]);
  const merge = async (t) => {
    if (files.length < 1 || !t.canUse) return;
    try {
      const out = await PDFDocument.create();
      for (const f of files) {
        const src = await PDFDocument.load(await f.arrayBuffer());
        const pages = await out.copyPages(src, src.getPageIndices());
        pages.forEach((p) => out.addPage(p));
      }
      const bytes = await out.save();
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "merged.pdf"; a.click();
      t.inc(); toast.success("PDFs merged");
    } catch { toast.error("Merge failed — ensure files are valid PDFs"); }
  };
  return (
    <ProGate tool="pdf" freeLimit={3}>
      {(t) => (
        <div className="space-y-5">
          <label className="block border-2 border-dashed border-slate-200 bg-slate-50/50 hover:bg-slate-50 rounded-xl p-8 text-center cursor-pointer transition-colors">
            <FileStack className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            {files.length ? `${files.length} PDF(s) selected` : "Select PDF files to merge (in order)"}
            <input data-testid="pdf-upload" type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files))} />
          </label>
          {files.length > 0 && <ol className="text-sm text-slate-500 list-decimal ml-5">{files.map((f, i) => <li key={i}>{f.name}</li>)}</ol>}
          <button data-testid="pdf-merge" onClick={() => merge(t)} disabled={files.length < 2} className={btn}><FileStack className="w-4 h-4" /> Merge PDFs</button>
          <p className="text-xs text-slate-400">Tip: select 2 or more PDFs to combine them into one document. All processing happens in your browser — files never leave your device.</p>
        </div>
      )}
    </ProGate>
  );
}

/* ---------------- COUNTER ---------------- */
function CounterTool() {
  const [text, setText] = useState("");
  const stats = useMemo(() => {
    const words = (text.trim().match(/\S+/g) || []).length;
    return { words, chars: text.length, sentences: (text.match(/[.!?]+/g) || []).length, read: Math.max(1, Math.ceil(words / 200)) };
  }, [text]);
  const title = (s) => s.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
  const sentence = (s) => s.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
  return (
    <div className="space-y-5">
      <textarea data-testid="counter-text" rows={8} className={input} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste or type your text…" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="counter-stats">
        {[["Words", stats.words], ["Characters", stats.chars], ["Sentences", stats.sentences], ["Read time", stats.read + " min"]].map(([l, v]) => (
          <div key={l} className="border border-slate-100 rounded-xl p-4 text-center"><div className="font-display text-2xl text-indigo-600">{v}</div><div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{l}</div></div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button className={ghost} onClick={() => setText(text.toUpperCase())}>UPPERCASE</button>
        <button className={ghost} onClick={() => setText(text.toLowerCase())}>lowercase</button>
        <button className={ghost} onClick={() => setText(title(text))}>Title Case</button>
        <button className={ghost} onClick={() => setText(sentence(text))}>Sentence case</button>
        <button className={ghost} onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }}><Copy className="w-4 h-4" /> Copy</button>
      </div>
    </div>
  );
}

/* ---------------- PASSWORD ---------------- */
function PasswordTool() {
  const [len, setLen] = useState(16);
  const [opts, setOpts] = useState({ upper: true, lower: true, num: true, sym: true });
  const [pw, setPw] = useState("");
  const gen = () => {
    let set = "";
    if (opts.upper) set += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    if (opts.lower) set += "abcdefghijklmnopqrstuvwxyz";
    if (opts.num) set += "0123456789";
    if (opts.sym) set += "!@#$%^&*()-_=+[]{}";
    if (!set) return toast.error("Select at least one option");
    let out = ""; const arr = new Uint32Array(len); crypto.getRandomValues(arr);
    for (let i = 0; i < len; i++) out += set[arr[i] % set.length];
    setPw(out);
  };
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 border border-slate-100 rounded-xl p-4 bg-slate-50/40">
        <code data-testid="pw-result" className="font-mono text-lg flex-1 break-all">{pw || "Click generate…"}</code>
        {pw && <button onClick={() => { navigator.clipboard.writeText(pw); toast.success("Copied"); }} className="text-indigo-600"><Copy className="w-5 h-5" /></button>}
      </div>
      <div><label className={label}>Length: {len}</label><input type="range" min="6" max="48" value={len} onChange={(e) => setLen(Number(e.target.value))} className="w-full accent-indigo-600" /></div>
      <div className="flex flex-wrap gap-4">
        {[["upper", "A-Z"], ["lower", "a-z"], ["num", "0-9"], ["sym", "!@#"]].map(([k, l]) => (
          <label key={k} className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={opts[k]} onChange={(e) => setOpts({ ...opts, [k]: e.target.checked })} className="accent-indigo-600 w-4 h-4" /> {l}</label>
        ))}
      </div>
      <button data-testid="pw-generate" onClick={gen} className={btn}><KeyRound className="w-4 h-4" /> Generate Password</button>
    </div>
  );
}

/* ---------------- META TAGS ---------------- */
function MetaTool() {
  const [d, setD] = useState({ title: "", desc: "", url: "https://juliusmason.com", image: "" });
  const tags = `<title>${d.title}</title>
<meta name="description" content="${d.desc}" />
<meta property="og:title" content="${d.title}" />
<meta property="og:description" content="${d.desc}" />
<meta property="og:url" content="${d.url}" />
<meta property="og:image" content="${d.image}" />
<meta name="twitter:card" content="summary_large_image" />`;
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="space-y-4">
        <div><label className={label}>Page title ({d.title.length}/60)</label><input data-testid="meta-title" className={input} value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} /></div>
        <div><label className={label}>Description ({d.desc.length}/160)</label><textarea rows={3} className={input} value={d.desc} onChange={(e) => setD({ ...d, desc: e.target.value })} /></div>
        <div><label className={label}>URL</label><input className={input} value={d.url} onChange={(e) => setD({ ...d, url: e.target.value })} /></div>
        <div><label className={label}>Image URL</label><input className={input} value={d.image} onChange={(e) => setD({ ...d, image: e.target.value })} /></div>
      </div>
      <div className="space-y-4">
        <div className="border border-slate-200 rounded-xl p-4"><p className="text-xs text-slate-400 mb-1">Google preview</p><p className="text-indigo-700 text-lg leading-tight">{d.title || "Your title here"}</p><p className="text-green-700 text-sm">{d.url}</p><p className="text-slate-600 text-sm">{d.desc || "Your meta description preview will appear here."}</p></div>
        <div className="relative"><pre className="bg-slate-900 text-slate-100 text-xs rounded-xl p-4 overflow-x-auto" data-testid="meta-output">{tags}</pre><button onClick={() => { navigator.clipboard.writeText(tags); toast.success("Copied"); }} className="absolute top-2 right-2 text-slate-300 hover:text-white"><Copy className="w-4 h-4" /></button></div>
      </div>
    </div>
  );
}

/* ---------------- FAX ---------------- */
function FaxTool() {
  return (
    <div className="space-y-5">
      <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-xl p-4 text-sm">
        Online fax sending requires a fax provider (e.g. Twilio or Documo). The form below is ready — add your provider API key in the admin Integrations panel to go live.
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={label}>Recipient fax number</label><input data-testid="fax-number" className={input} placeholder="+1 555 000 0000" /></div>
        <div><label className={label}>Your email (for confirmation)</label><input className={input} placeholder="you@email.com" /></div>
      </div>
      <label className="block border-2 border-dashed border-slate-200 bg-slate-50/50 rounded-xl p-8 text-center cursor-pointer"><Printer className="w-8 h-8 text-slate-400 mx-auto mb-2" />Attach a PDF to fax<input type="file" accept="application/pdf" className="hidden" /></label>
      <button onClick={() => toast.info("Add a fax provider key in admin to enable sending.")} className={btn}><Printer className="w-4 h-4" /> Send Fax</button>
    </div>
  );
}

export const TOOLS = [
  { slug: "qr-code", name: "QR Code Generator", desc: "Custom QR codes with your colours, downloadable as PNG.", icon: QrCode, category: "Marketing", pro: false, span: "md:col-span-2", Component: QRTool },
  { slug: "invoice", name: "Invoice Generator", desc: "Create clean, branded PDF invoices in seconds.", icon: FileText, category: "Business", pro: true, span: "md:col-span-1", Component: InvoiceTool },
  { slug: "image", name: "Image Compressor", desc: "Resize, compress & convert images (JPEG/PNG/WebP).", icon: ImageIcon, category: "Media", pro: false, span: "md:col-span-1", Component: ImageTool },
  { slug: "pdf", name: "PDF Merge & Split", desc: "Combine PDFs right in your browser — private & fast.", icon: FileStack, category: "Documents", pro: true, span: "md:col-span-2", Component: PDFTool },
  { slug: "word-counter", name: "Word & Character Counter", desc: "Counts, reading time, and case converters.", icon: Type, category: "Writing", pro: false, span: "md:col-span-1", Component: CounterTool },
  { slug: "password", name: "Password Generator", desc: "Strong, secure passwords generated locally.", icon: KeyRound, category: "Security", pro: false, span: "md:col-span-1", Component: PasswordTool },
  { slug: "meta-tags", name: "Meta Tag Generator", desc: "SEO & social meta tags with live search preview.", icon: Tags, category: "SEO", pro: false, span: "md:col-span-1", Component: MetaTool },
  { slug: "fax", name: "Online Fax", desc: "Send a fax from your browser (provider key required).", icon: Printer, category: "Business", pro: true, span: "md:col-span-1", Component: FaxTool },
  ...MORE_TOOLS,
  ...EXTRA_TOOLS,
  ...MICRO_TOOLS_2,
  ...CUSTOM_TOOLS_2,
];

export const getTool = (slug) => TOOLS.find((t) => t.slug === slug);
